import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";
import api from "@/lib/api";

function timeAgo(iso, t) {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return t("notifications.now");
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Translate a notification into the CURRENTLY selected UI language using its
  // stored `type` + `params`. Falls back to the server-stored title/message
  // for legacy notifications that have no recoverable params.
  const localize = useCallback((n) => {
    const k = n.type;
    const p = n.params;
    if (p && i18n.exists(`notifications.types.${k}.title`)) {
      return {
        title: t(`notifications.types.${k}.title`),
        message: t(`notifications.types.${k}.body`, { ...p, defaultValue: n.message }),
      };
    }
    return { title: n.title, message: n.message };
  }, [t, i18n]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    if (document.hidden) return;
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 45000);
    const onVisible = () => { if (!document.hidden) fetchNotifs(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [fetchNotifs]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="notification-bell"
        className="relative p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={19} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[17px] h-[17px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold font-mono-fin"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="fixed left-3 right-3 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[340px] rounded-2xl border border-slate-200 bg-white shadow-2xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-display font-bold text-slate-900">{t("notifications.title")}</span>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notification-mark-all"
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
              >
                <CheckCheck size={14} /> {t("notifications.mark_all")}
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-400" data-testid="notification-empty">
                <Inbox size={28} />
                <span className="text-sm">{t("notifications.empty")}</span>
              </div>
            ) : (
              items.map((n) => {
                const { title, message } = localize(n);
                return (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${n.read ? "" : "bg-blue-50/40"}`}
                >
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-blue-600"}`} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">{title}</span>
                      <span className="text-[10px] font-mono-fin text-slate-400 shrink-0">{timeAgo(n.created_at, t)}</span>
                    </span>
                    <span className="block text-xs text-slate-600 mt-0.5 leading-snug break-words">{message}</span>
                  </span>
                  {!n.read && <Check size={14} className="text-slate-300 shrink-0 mt-1" />}
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
